/**
 * Keyboard shortcuts utility for accessibility
 * Provides common keyboard shortcuts and handlers
 */

/**
 * Keyboard shortcut keys
 */
export const SHORTCUTS = {
  SKIP_TO_MAIN: "m",
  SKIP_TO_NAV: "n",
  SKIP_TO_FOOTER: "f",
} as const;

/**
 * Handles keyboard shortcuts with modifier keys
 * @param event - Keyboard event
 * @param shortcut - The shortcut key to check
 * @param callback - Function to call when shortcut is pressed
 * @param modifiers - Modifier keys required (default: none)
 */
export function handleKeyboardShortcut(
  event: KeyboardEvent,
  shortcut: string,
  callback: () => void,
  modifiers: { alt?: boolean; ctrl?: boolean; shift?: boolean; meta?: boolean } = {}
): void {
  const { alt = false, ctrl = false, shift = false, meta = false } = modifiers;

  // Guard: Return early if event.key is undefined, null, or not a string (common on mobile browsers)
  // Check explicitly for undefined/null and ensure it's a string before calling toLowerCase
  if (event.key == null || typeof event.key !== "string") {
    return;
  }

  const keyMatches = event.key.toLowerCase() === shortcut.toLowerCase();

  if (
    keyMatches &&
    event.altKey === alt &&
    event.ctrlKey === ctrl &&
    event.shiftKey === shift &&
    event.metaKey === meta
  ) {
    // Don't trigger if user is typing in an input field
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    ) {
      return;
    }

    event.preventDefault();
    callback();
  }
}

/**
 * Sets up global keyboard shortcuts
 */
export function setupKeyboardShortcuts(): () => void {
  const handleKeyDown = (event: KeyboardEvent) => {
    // Alt + M: Skip to main content
    handleKeyboardShortcut(
      event,
      SHORTCUTS.SKIP_TO_MAIN,
      () => {
        const main = document.querySelector("main");
        if (main instanceof HTMLElement) {
          main.focus();
          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;
          main.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
        }
      },
      { alt: true }
    );

    // Alt + N: Skip to navigation
    handleKeyboardShortcut(
      event,
      SHORTCUTS.SKIP_TO_NAV,
      () => {
        const nav = document.querySelector("nav");
        if (nav instanceof HTMLElement) {
          nav.focus();
          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;
          nav.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
        }
      },
      { alt: true }
    );

    // Alt + F: Skip to footer
    handleKeyboardShortcut(
      event,
      SHORTCUTS.SKIP_TO_FOOTER,
      () => {
        const footer = document.querySelector("footer");
        if (footer instanceof HTMLElement) {
          footer.focus();
          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;
          footer.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "end",
          });
        }
      },
      { alt: true }
    );
  };

  document.addEventListener("keydown", handleKeyDown);

  // Return cleanup function
  return () => {
    document.removeEventListener("keydown", handleKeyDown);
  };
}
